import { accountReloadSuppressed } from '@/utils/wallet/account-change-guard'
import {
	STORAGE_TAPROOT_ADDRESS,
	STORAGE_PUBLIC_KEY,
	STORAGE_WALLET_TYPE,
	MOBILE_BREAKPOINT,
	NETWORK
} from '@/utils/constants'
import SafeLocalStorage from '../safe-local-storage'
import { extractFinalizedPsbtHex, normalizePsbtHex } from './psbt'
import Wallet from './'
import { WalletProvider } from '@/utils/types/wallet'

import { isUserRejection } from './signing-errors'

export default class OKXWallet implements WalletProvider {
	get name(): string {
		return 'OKX'
	}

	get capabilities(): string[] {
		return []
	}

	get connectIcon(): string {
		return '/connect-okx.svg'
	}

	get enabled(): boolean {
		return NETWORK === 'BTC'
	}

	async connect(): Promise<boolean> {
		// @ts-ignore
		if (!window.okxwallet) {
			const isMobile = window.innerWidth < MOBILE_BREAKPOINT

			if (isMobile) {
				const dappUrl = encodeURIComponent(window.location.href)
				window.location.href = `okx://wallet/dapp/url?dappUrl=${dappUrl}`
				return false
			}

			window.open('https://www.okx.com/web3', '_blank')
			return false
		}

		await this.login()
		return true
	}

	async login(): Promise<void> {
		// @ts-ignore
		if (!window.okxwallet.bitcoin) {
			throw new Error('OKX is not installed. Install the extension, then reconnect.')
		}

		const { address, pubkey } = await (window as any).okxwallet.bitcoin
			.requestAccounts()
			.then(async ([address]: string[]) => ({
				address,
				pubkey: await (window as any).okxwallet.bitcoin.getPublicKey()
			}))

		localStorage.setItem(STORAGE_TAPROOT_ADDRESS, address)
		localStorage.setItem(STORAGE_WALLET_TYPE, 'okx')
		localStorage.setItem(STORAGE_PUBLIC_KEY, pubkey)
		try {
			await Wallet.fetchProfile()
		} catch (_e) {}
	}

	async listenForAccountChange(): Promise<void> {
		// @ts-ignore
		if (!window.okxwallet || !window.okxwallet.bitcoin || Wallet.type() !== 'okx') {
			return
		}

		const isMobile = window.innerWidth < MOBILE_BREAKPOINT

		if (isMobile) {
			// @ts-ignore
			const addresses = await window.okxwallet.bitcoin.requestAccounts()

			if (addresses[0] != this.taprootAddress()) {
				await this.login()
				window.location.reload()
			}
		}

		// @ts-ignore
		window.okxwallet.bitcoin.on('accountsChanged', async () => {
			await this.login()
			if (accountReloadSuppressed()) return
			window.location.reload()
		})
	}

	async dummyPsbt(inscription_id: string): Promise<string> {
		const psbt = await Wallet.getEscrow(inscription_id, 1e8 * 21000000, {})
		return psbt
	}

	taprootAddress(): string {
		return SafeLocalStorage.getItem(STORAGE_TAPROOT_ADDRESS)
	}

	taprootPublicKey(): string {
		return SafeLocalStorage.getItem(STORAGE_PUBLIC_KEY)
	}

	async signPSBTS(psbts: string[]): Promise<string[]> {
		try {
			// @ts-ignore
			const data = await window.okxwallet.bitcoin.signPsbts(psbts)
			return data
		} catch (e) {
			console.log(e)
			throw e
		}
	}

	async signPSBT(psbt: string): Promise<string> {
		try {
			// @ts-ignore
			const data = await window.okxwallet.bitcoin.signPsbt(psbt)
			return normalizePsbtHex(data)
		} catch (e) {
			console.log(e)
			throw e
		}
	}

	async signPSBTExtractTx(psbt: string): Promise<string> {
		try {
			// @ts-ignore
			const data = await this.signPSBT(psbt)
			return extractFinalizedPsbtHex(data)
		} catch (e) {
			console.log(e)
			throw e
		}
	}

	async signMessage(message: String): Promise<string> {
		try {
			// @ts-ignore
			const res = await window.okxwallet.bitcoin.signMessage(message, 'bip322-simple')
			return res
		} catch (_e) {
			return undefined as unknown as string
		}
	}

	async signInscribe(psbt: string): Promise<string> {
		return this.signPSBTExtractTx(psbt)
	}

	async signTransfer(psbt: string): Promise<string> {
		return this.signPSBTExtractTx(psbt)
	}

	async signEscrow(psbt: string): Promise<string> {
		return this.signPSBT(psbt)
	}

	async signPurchase(
		setup_psbt: string,
		purchase_psbt: string
	): Promise<{ signedSetup: string; signedPurchase: string }> {
		try {
			let signedSetup = ''

			if (setup_psbt) {
				// @ts-ignore
				const signedSetupPSBT = await this.signPSBT(setup_psbt)
				signedSetup = extractFinalizedPsbtHex(signedSetupPSBT)
			}
			// @ts-ignore
			const signedPurchase = await window.okxwallet.bitcoin.signPsbt(purchase_psbt)
			return { signedSetup, signedPurchase }
		} catch (e) {
			if (isUserRejection(e)) {
				return undefined as unknown as { signedSetup: string; signedPurchase: string }
			}
			console.log(e)
			throw e
		}
	}
}
